import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Star, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Public evaluation response page — accessed via token link
// /evaluation/:send_id?token=...
export default function EvaluationRespondPage() {
  const { send_id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError("Lien invalide"); setLoading(false); return; }
    axios.get(`${API}/api/evaluations/respond/${send_id}?token=${encodeURIComponent(token)}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || "Erreur"); setLoading(false); });
  }, [send_id, token]);

  const submit = async () => {
    if (!data) return;
    // Validate required
    for (const q of data.template.questions || []) {
      if (q.required && (answers[q.question_id] === undefined || answers[q.question_id] === "" || (Array.isArray(answers[q.question_id]) && answers[q.question_id].length === 0))) {
        toast.error(`Question obligatoire: ${q.label}`); return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/evaluations/respond/${send_id}/submit`, { token, answers });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0b0811]"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0b0811] text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center bg-[#14101c] border border-red-500/20 rounded-2xl p-8">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
        <h1 className="text-2xl font-bold mb-2">Lien invalide</h1>
        <p className="text-slate-400 text-sm">{error}</p>
      </div>
    </div>
  );
  if (done || data?.already_submitted) return (
    <div className="min-h-screen bg-[#0b0811] text-white flex items-center justify-center p-8" data-testid="eval-thanks">
      <div className="max-w-md text-center bg-[#14101c] border border-green-500/20 rounded-2xl p-8">
        <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-3" />
        <h1 className="text-2xl font-bold mb-2">Merci pour votre retour !</h1>
        <p className="text-slate-400 text-sm">Votre reponse a bien ete enregistree.</p>
      </div>
    </div>
  );

  const { template, session, program } = data;

  return (
    <div className="min-h-screen bg-[#0b0811] text-white p-6 md:p-12" data-testid="eval-respond-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="text-xs text-[#f7c935] font-semibold uppercase tracking-wider mb-1">
            {template.eval_type === "hot" ? "Evaluation a chaud" :
             template.eval_type === "cold" ? "Evaluation a froid" :
             template.eval_type === "pre" ? "Evaluation pre-formation" : "Questionnaire manager"}
          </div>
          <h1 className="text-3xl font-black tracking-tighter">{template.name}</h1>
          <div className="mt-2 text-sm text-slate-400">{program.title}{program.sous_titre && ` — ${program.sous_titre}`}</div>
          <div className="text-xs text-slate-500">{session.start_date} → {session.end_date}</div>
          {template.description && <p className="text-sm text-slate-300 mt-3">{template.description}</p>}
        </div>

        <div className="space-y-4">
          {(template.questions || []).map((q, i) => (
            <div key={q.question_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`q-${i}`}>
              <div className="font-semibold mb-3">
                {i + 1}. {q.label}
                {q.required && <span className="text-red-400 ml-1">*</span>}
              </div>
              <QuestionInput q={q} value={answers[q.question_id]} onChange={v => setAnswers(a => ({ ...a, [q.question_id]: v }))} />
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={submit} disabled={submitting} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="submit-eval-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer mes reponses
          </Button>
        </div>
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.type === "rating") {
    const max = q.max_value || 5;
    const min = q.min_value || 1;
    return (
      <div className="flex gap-1">
        {Array.from({ length: max - min + 1 }, (_, i) => i + min).map(n => (
          <button key={n} onClick={() => onChange(n)} className={`w-10 h-10 rounded ${value === n ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`} data-testid={`rate-${n}`}>
            <Star className={`w-5 h-5 mx-auto ${value === n ? "fill-current" : ""}`} />
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "nps") {
    return (
      <div>
        <div className="flex gap-1 flex-wrap">
          {Array.from({ length: 11 }, (_, n) => n).map(n => (
            <button key={n} onClick={() => onChange(n)} className={`w-9 h-9 rounded text-sm ${value === n ? "bg-[#f7c935] text-[#14101c] font-bold" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`nps-${n}`}>
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>Pas du tout</span><span>Totalement</span></div>
      </div>
    );
  }
  if (q.type === "radio") {
    return (
      <div className="space-y-2">
        {(q.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer hover:text-[#f7c935]">
            <input type="radio" checked={value === o} onChange={() => onChange(o)} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "checkbox") {
    const cur = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {(q.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer hover:text-[#f7c935]">
            <input type="checkbox" checked={cur.includes(o)} onChange={e => onChange(e.target.checked ? [...cur, o] : cur.filter(x => x !== o))} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "yesno") {
    return (
      <div className="flex gap-2">
        <Button onClick={() => onChange("oui")} className={value === "oui" ? "bg-green-500 text-white" : "bg-white/5 text-slate-400"}>Oui</Button>
        <Button onClick={() => onChange("non")} className={value === "non" ? "bg-red-500 text-white" : "bg-white/5 text-slate-400"}>Non</Button>
      </div>
    );
  }
  // text
  return <Textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={3} className="bg-[#0b0811] border border-white/10 text-white" placeholder="Votre reponse..." />;
}
